// Training Bucket — a persistent, manager-scoped "shopping cart" for content
// the manager wants to assign for training. A manager browses the project
// folder tree, right-clicks folders and/or worksnips to drop them in the
// bucket, then opens the Learning Center's "Bucket" tab to assign the whole
// batch to users/groups with a due date.
//
// Persisted to localStorage (key `worksnip.trainingBucket`) so the bucket
// survives reloads + cross-page navigation while the manager keeps browsing —
// mirrors how stores/authStore.ts wraps create() in persist(). Items are the
// same {type:'guide'|'project'; id} shape the assign endpoint takes, plus a
// cached label for chips/lists so the tab doesn't have to re-resolve names.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** One thing in the bucket. `type` matches trainingApi's TrainingItem:
 *  a snip is a 'guide', a folder is a 'project'. `label` is a display cache. */
export interface BucketItem {
  type: 'guide' | 'project';
  id: string;
  label: string;
}

interface TrainingBucketState {
  items: BucketItem[];
  /** Add an item; no-op if an item with the same type+id is already present. */
  add: (item: BucketItem) => void;
  /** Remove the item matching type+id. */
  remove: (type: 'guide' | 'project', id: string) => void;
  /** Add if absent, remove if present (right-click toggle). */
  toggle: (item: BucketItem) => void;
  /** Empty the bucket. */
  clear: () => void;
  /** True when an item with this type+id is in the bucket. */
  has: (type: 'guide' | 'project', id: string) => boolean;
}

const keyOf = (type: string, id: string) => `${type}:${id}`;

export const useTrainingBucket = create<TrainingBucketState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (item) =>
        set((s) => {
          if (s.items.some((b) => b.type === item.type && b.id === item.id)) return s;
          return { items: [...s.items, item] };
        }),
      remove: (type, id) =>
        set((s) => ({ items: s.items.filter((b) => !(b.type === type && b.id === id)) })),
      toggle: (item) =>
        set((s) => {
          const exists = s.items.some((b) => b.type === item.type && b.id === item.id);
          return exists
            ? { items: s.items.filter((b) => !(b.type === item.type && b.id === item.id)) }
            : { items: [...s.items, item] };
        }),
      clear: () => set({ items: [] }),
      has: (type, id) => get().items.some((b) => keyOf(b.type, b.id) === keyOf(type, id)),
    }),
    { name: 'worksnip.trainingBucket' },
  ),
);

/** Convenience selector for the live count — keeps Zustand's primitive
 *  equality check happy (no object selector) so the AppShell indicator only
 *  re-renders when the number actually changes. */
export const selectBucketCount = (s: TrainingBucketState) => s.items.length;
